import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import moment from "moment";
import { message } from "antd";

import Styled from "../ReviewInvoice/ReviewInvoiceStyle";
import Colors from "../../../../Constants/Colors";
import TextStyles from "../../../../Constants/TextStyles";

import { getFileFromUrl } from "../../Commons/utils";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import LoaderSpin from "../../Commons/LoaderSpin";
import { getRequest } from "../../../../axios/axios";

const InvoicePublic = () => {
  const params = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [invoiceData, setInvoiceData] = useState({});
  const [isPDFDownloading, setIsPDFDownloading] = useState(false);

  const invoiceId = params.id;
  const { invoice, user: data, businessInfo, brand } = invoiceData;

  const invoice_services_array = invoice && invoice.invoice_services;
  const line_item_services_array = invoice && invoice.line_item_services;
  const taxes_array = invoice && invoice.taxes;

  const clientDetails =
    invoice && invoice.client_id
      ? [
          invoice.client_id.street ? invoice.client_id.street : "",
          invoice.client_id.suite ? invoice.client_id.suite : "",
          invoice.client_id.city ? invoice.client_id.city : "",
          invoice.client_id.zip_code ? invoice.client_id.zip_code : "",
          invoice.client_id.state ? invoice.client_id.state : "",
          invoice.client_id.country ? invoice.client_id.country : "",
        ]
          .filter((item) => item)
          .join(", ")
      : "";

  const businessDetails = businessInfo
    ? [
        businessInfo.street ? businessInfo.street : "",
        businessInfo.suite ? businessInfo.suite : "",
        businessInfo.city ? businessInfo.city : "",
        businessInfo.zip_code ? businessInfo.zip_code : "",
        businessInfo.state ? businessInfo.state : "",
        businessInfo.country ? businessInfo.country : "",
      ]
        .filter((item) => item)
        .join(", ")
    : "";

  const totalInvoiceServiceAmount = useMemo(() => {
    let sum = 0;
    invoice_services_array &&
      invoice_services_array.forEach((d) => {
        if (d.service_rate !== "" && d.checked) {
          sum += parseFloat(d.service_rate) * parseFloat(d.service_rate_count);
        }
      });
    return sum;
  }, [invoice_services_array]);

  const totalLineItemServiceAmount = useMemo(() => {
    let sum = 0;
    line_item_services_array &&
      line_item_services_array.forEach((d) => {
        if (d.service_rate !== "") {
          sum += parseFloat(d.service_rate) * parseFloat(d.service_rate_count);
        }
      });
    return sum;
  }, [line_item_services_array]);

  const totalServiceAmount = useMemo(() => {
    return totalInvoiceServiceAmount + totalLineItemServiceAmount;
  }, [totalInvoiceServiceAmount, totalLineItemServiceAmount]);

  const invoiceDiscount = useMemo(() => {
    let sum = 0;
    if (invoice && invoice.discount) {
      if (invoice.discount.percentage) {
        sum = parseFloat(
          totalServiceAmount * parseFloat(invoice.discount.percentage) * 0.01
        );
      } else {
        sum = invoice.discount.value;
      }
    }
    return sum;
  }, [totalServiceAmount, invoice]);

  const totalServiceAmountAfterDiscount = useMemo(() => {
    return parseFloat(totalServiceAmount) - parseFloat(invoiceDiscount);
  }, [totalServiceAmount, invoiceDiscount]);

  const totalTaxesAmount = useMemo(() => {
    let sum = 0;
    taxes_array &&
      taxes_array.forEach((d) => {
        if (d.percentage !== "") {
          sum +=
            (parseFloat(d.percentage) *
              parseFloat(totalServiceAmountAfterDiscount)) /
            100;
        }
      });
    return sum;
  }, [taxes_array, totalServiceAmountAfterDiscount]);

  const totalDueAmount = useMemo(() => {
    const totalDueAmount = totalServiceAmountAfterDiscount + totalTaxesAmount;
    return totalDueAmount;
  }, [totalServiceAmountAfterDiscount, totalTaxesAmount]);

  const downloadInvoicePDF = async () => {
    try {
      setIsPDFDownloading(true);
      
      const { data } = await getRequest(`/invoices/${invoiceId}/download`);

      if (data && data.success) {
        getFileFromUrl(data.data.url, "Invoice").then((res) => {
          const href = URL.createObjectURL(res);
          const aElement = document.createElement("a");
          aElement.href = href;
          aElement.setAttribute("download", "Invoice");
          aElement.setAttribute("target", "_blank");
          aElement.click();
          URL.revokeObjectURL(href);
        });
      }
    } catch (err) {
      message.error("Failed to download the invoice");
    } finally {
      setIsPDFDownloading(false);
    }
  };

  const viewInvoice = async function () {
    try {
      setIsLoading(true);

      const { data } = await getRequest(`/invoices/${invoiceId}/public`);

      if (data.success) {
        setInvoiceData(data.data);
      }
    } catch (err) {
      message.error("Failed to view invoice");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    viewInvoice();
  }, [invoiceId]);

  const brandImage = brand && brand.brand_logo && brand.brand_logo.file_url;

  return (
    <>
      <TextStyles.FontSize24px
        className="fw-medium text-center fixed-top bg-white p-3"
        style={{
          borderBottom: "2px solid #dfe1e9",
        }}
      >
        View Invoice
      </TextStyles.FontSize24px>

      <div className="p-3 p-md-5" style={{ marginTop: "4rem" }}>
        {isLoading && (
          <TextStyles.FontSize16px className="text-center">
            Please wait...
          </TextStyles.FontSize16px>
        )}

        {!isLoading && Object.keys(invoiceData).length > 0 && (
          <Styled.SetupBox
            bgColor={brand && brand.brand_color && brand.brand_color}
            style={{
              maxWidth: 800,
              margin: "0 auto",
              boxShadow: "0 0 2px 1px rgba(0, 0, 0, 0.2)",
            }}
          >
            <div className="d-flex align-items-center justify-content-between gap-2 my-2">
              {invoice && invoice.invoice_title ? (
                <TextStyles.FontSize20px
                  className="review_box_padding_left review_box_padding_right font-weight-600"
                  style={{
                    color:
                      brand && brand.brand_color
                        ? brand.brand_color
                        : Colors.primary,
                  }}
                >
                  {invoice.invoice_title}
                </TextStyles.FontSize20px>
              ) : null}

              <div
                onClick={() => {
                  if (!isPDFDownloading) downloadInvoicePDF();
                }}
              >
                {isPDFDownloading ? (
                  <LoaderSpin
                    className="text-muted text-opacity-10"
                    spinnerSize="spinner-border-sm"
                  />
                ) : (
                  <SaveAltIcon
                    sx={{
                      color: Colors.font2,
                      fontSize: 27,
                      opacity: "0.8",
                      "&:hover": { color: Colors.bgColorFrontend },
                      cursor: "pointer",
                    }}
                  />
                )}
              </div>
            </div>

            {brandImage ? (
              <Styled.BrandLogoBox
                className="cursor-default review_box_padding mb-2"
                logoAlignment={brand.logo_alignment || "center"}
              >
                <img
                  src={brand.brand_logo.file_url}
                  alt="brand logo"
                  style={{ maxHeight: 100 }}
                />
              </Styled.BrandLogoBox>
            ) : null}

            <div className={`d-flex flex-wrap flex-sm-nowrap gap-3 gap-md-4 justify-content-between mb-4 ${brandImage ? '' : 'mt-5'}`}>
              <div className="review_box_padding_left invoice_business order-1 order-sm-0">
                <TextStyles.FontSize16px className="font-weight-600">
                  From
                </TextStyles.FontSize16px>

                <>
                  {businessInfo && businessInfo.business_name ? (
                    <TextStyles.FontSize14px>
                      {businessInfo.business_name}
                    </TextStyles.FontSize14px>
                  ) : (
                    <TextStyles.FontSize14px>
                      {data &&
                      data.firstName &&
                      data.lastName &&
                      data.country &&
                      data.country.code
                        ? `${data.firstName} ${data.lastName} (${data.country.code}`
                        : ""}
                    </TextStyles.FontSize14px>
                  )}

                  {data && data.email && (
                    <TextStyles.FontSize14px>
                      {data.email}
                    </TextStyles.FontSize14px>
                  )}

                  {businessInfo !== null && (
                    <>
                      {businessInfo &&
                        businessInfo.phone_number &&
                        businessInfo.phone_code && (
                          <TextStyles.FontSize14px>
                            +{businessInfo.phone_code.country_code}
                            {businessInfo.phone_number}
                          </TextStyles.FontSize14px>
                        )}
                      <TextStyles.FontSize14px>
                        {businessDetails}
                      </TextStyles.FontSize14px>

                      {businessInfo && businessInfo.tax_id_label && (
                        <TextStyles.FontSize14px>
                          <span>Tax Label: </span>
                          {businessInfo.tax_id_label}
                        </TextStyles.FontSize14px>
                      )}

                      {businessInfo && businessInfo.tax_id_number && (
                        <TextStyles.FontSize14px>
                          <span>Tax ID: </span>
                          {businessInfo.tax_id_number}
                        </TextStyles.FontSize14px>
                      )}
                    </>
                  )}
                </>
              </div>

              <div className="review_box_padding_right order-0 order-sm-1">
                <TextStyles.FontSize14px className="font-weight-600">
                  Amount due
                </TextStyles.FontSize14px>

                <TextStyles.FontSize14px
                  className="text-sm-end"
                  style={{
                    color:
                      brand && brand.brand_color
                        ? brand.brand_color
                        : Colors.primary,
                  }}
                >
                  {invoice && invoice.currency && invoice.currency.symbol}
                  {totalDueAmount > 0
                    ? parseFloat(totalDueAmount).toFixed(2)
                    : "00.00"}
                </TextStyles.FontSize14px>
              </div>
            </div>

            <div className="d-flex flex-wrap flex-sm-nowrap gap-3 gap-md-4 justify-content-between mb-3">
              <div className="review_box_padding_left invoice_client_info edit_elient_info_box">
                <TextStyles.FontSize16px className="font-weight-600">
                  To
                </TextStyles.FontSize16px>

                <TextStyles.FontSize14px>
                  {invoice &&
                    invoice.client_id &&
                    invoice.client_id.company_name}
                </TextStyles.FontSize14px>

                <TextStyles.FontSize14px>
                  {clientDetails}
                </TextStyles.FontSize14px>

                {invoice &&
                  invoice.client_id &&
                  invoice.client_id.tax_id_label && (
                    <TextStyles.FontSize14px>
                      <span>Tax Label: </span>
                      {invoice.client_id.tax_id_label}
                    </TextStyles.FontSize14px>
                  )}

                {invoice &&
                  invoice.client_id &&
                  invoice.client_id.tax_id_number && (
                    <TextStyles.FontSize14px>
                      <span>Tax ID: </span>
                      {invoice.client_id.tax_id_number}
                    </TextStyles.FontSize14px>
                  )}
              </div>

              <div className="edit_invoice_dates review_box_padding_right">
                <TextStyles.FontSize14px className="text-sm-end font-weight-600 mt-3 mt-sm-0">
                  Invoice date
                </TextStyles.FontSize14px>

                <TextStyles.FontSize14px className="text-sm-end">
                  {invoice && invoice.issued
                    ? moment(invoice.issued).format("DD MMM YYYY")
                    : moment().format("DD MMM YYYY")}
                </TextStyles.FontSize14px>

                <TextStyles.FontSize14px className="text-sm-end font-weight-600 mt-3">
                  Due date
                </TextStyles.FontSize14px>

                <TextStyles.FontSize14px className="text-sm-end">
                  {invoice &&
                  invoice.issued &&
                  invoice.due_date &&
                  moment(invoice.due_date).format("DD MMM YYYY")}
                </TextStyles.FontSize14px>
              </div>
            </div>

            <div className="review_box_padding">
              <div
                className="mt-3 w-100"
                style={{
                  height: "1px",
                  border: "0.5px solid #efefef",
                }}
              />
            </div>

            {invoice.invoice_services.length > 0 && (
              <div className="review_box_padding my-3">
                <TextStyles.FontSize14px className="font-weight-600 mb-1">
                  Project
                </TextStyles.FontSize14px>

                <TextStyles.FontSize14px>
                  {invoice &&
                    invoice.project_id &&
                    invoice.project_id.project_name}
                </TextStyles.FontSize14px>
              </div>
            )}

            {invoice.invoice_services.length > 0 && (
              <div className="edit_invoice_service review_box_table_padding">
                <div
                  className="d-flex font-weight-600 gap-2"
                  style={{
                    background: Colors.tableHeadColor,
                    padding: "0.8rem 1rem",
                    borderRadius: "0.5rem",
                  }}
                >
                  <TextStyles.FontSize14px
                    className="d-flex w-100"
                    style={{
                      minWidth: 50,
                    }}
                  >
                    <span className="three-dots">Description</span>
                  </TextStyles.FontSize14px>
                  <TextStyles.FontSize14px
                    className="d-flex justify-content-center w-100"
                    style={{ maxWidth: 60, minWidth: 50 }}
                  >
                    <span className="three-dots">Qty</span>
                  </TextStyles.FontSize14px>
                  <TextStyles.FontSize14px
                    className="d-flex justify-content-end w-100"
                    style={{ maxWidth: 110, minWidth: 50 }}
                  >
                    <span className="three-dots">Price</span>
                  </TextStyles.FontSize14px>
                  <TextStyles.FontSize14px
                    className="d-flex justify-content-end w-100"
                    style={{ maxWidth: 120, minWidth: 50 }}
                  >
                    <span className="three-dots">Amount</span>
                  </TextStyles.FontSize14px>
                </div>

                {invoice &&
                  invoice.invoice_services &&
                  invoice.invoice_services
                    .filter((item) => item.checked)
                    .map((data, index) => {
                      return (
                        <div key={index}>
                          <div
                            className="d-flex gap-2"
                            style={{ padding: "0.8rem 1rem" }}
                          >
                            <div
                              className="d-flex flex-column w-100"
                              style={{
                                minWidth: 50,
                              }}
                            >
                              <TextStyles.FontSize14px className="three-dots mb-1">
                                <span className="three-dots font-weight-500">
                                  {data.service_name}
                                </span>
                              </TextStyles.FontSize14px>
                              <TextStyles.FontSize14px
                                className="d-none d-md-block"
                                style={{
                                  whiteSpace: "break-spaces",
                                  maxWidth: "20rem",
                                }}
                              >
                                <span className="truncate-text">
                                  {data.desc}
                                </span>
                              </TextStyles.FontSize14px>
                            </div>
                            <TextStyles.FontSize14px
                              className="d-flex justify-content-center w-100"
                              style={{ maxWidth: 60, minWidth: 50 }}
                            >
                              <span className="three-dots">
                                {data.service_rate_count}
                              </span>
                            </TextStyles.FontSize14px>

                            <TextStyles.FontSize14px
                              className="d-flex justify-content-end w-100"
                              style={{ maxWidth: 110, minWidth: 50 }}
                            >
                              <span className="three-dots">
                                {" "}
                                {invoice &&
                                  invoice.currency &&
                                  invoice.currency.symbol}
                                {data.service_rate
                                  ? parseFloat(data.service_rate).toFixed(2)
                                  : "00.00"}
                              </span>{" "}
                            </TextStyles.FontSize14px>

                            <TextStyles.FontSize14px
                              className="d-flex justify-content-end w-100"
                              style={{ maxWidth: 120, minWidth: 50 }}
                            >
                              <span className="three-dots">
                                {invoice &&
                                  invoice.currency &&
                                  invoice.currency.symbol}

                                {data.service_rate && data.service_rate_count
                                  ? parseFloat(
                                      data.service_rate *
                                        data.service_rate_count
                                    ).toFixed(2)
                                  : "00.00"}
                              </span>
                            </TextStyles.FontSize14px>
                          </div>
                          <div className="d-flex" style={{ padding: "0 1rem" }}>
                            <div
                              style={{
                                width: "100%",
                                height: "1px",
                                border: "0.5px solid #efefef",
                              }}
                            />
                          </div>
                        </div>
                      );
                    })}
              </div>
            )}

            {invoice.line_item_services.length > 0 && (
              <div>
                <div className="d-flex review_box_padding py-3">
                  <TextStyles.FontSize14px className="font-weight-600">
                    {invoice &&
                    invoice.project_id &&
                    invoice.project_id.project_name
                      ? "Other services"
                      : "Services"}
                  </TextStyles.FontSize14px>
                </div>
              </div>
            )}

            {invoice.line_item_services.length > 0 &&
              invoice.invoice_services.length > 0 && (
                <div className="review_box_table_padding">
                  <div className="d-flex" style={{ padding: "0rem 1rem" }}>
                    <div
                      style={{
                        width: "100%",
                        height: "1px",
                        border: "0.5px solid #efefef",
                      }}
                    />
                  </div>
                </div>
              )}

            {invoice.line_item_services.length > 0 &&
              invoice.invoice_services.length === 0 && (
                <div className="review_box_table_padding">
                  <div
                    className="d-flex font-weight-600 gap-2"
                    style={{
                      background: Colors.tableHeadColor,
                      padding: "0.8rem 1rem",
                      borderRadius: "0.5rem",
                    }}
                  >
                    <TextStyles.FontSize14px
                      className="d-flex w-100"
                      style={{
                        minWidth: 50,
                      }}
                    >
                      <span className="three-dots">Description</span>
                    </TextStyles.FontSize14px>
                    <TextStyles.FontSize14px
                      className="d-flex justify-content-center w-100"
                      style={{ maxWidth: 60, minWidth: 50 }}
                    >
                      <span className="three-dots">Qty</span>
                    </TextStyles.FontSize14px>
                    <TextStyles.FontSize14px
                      className="d-flex justify-content-end w-100"
                      style={{ maxWidth: 110, minWidth: 50 }}
                    >
                      <span className="three-dots">Price</span>
                    </TextStyles.FontSize14px>
                    <TextStyles.FontSize14px
                      className="d-flex justify-content-end w-100"
                      style={{ maxWidth: 120, minWidth: 50 }}
                    >
                      <span className="three-dots">Amount</span>
                    </TextStyles.FontSize14px>
                  </div>
                </div>
              )}

            {invoice.line_item_services.length > 0 && (
              <div>
                {invoice &&
                  invoice.line_item_services &&
                  invoice.line_item_services.map((data, index) => {
                    return (
                      <div
                        className="edit_line_item_service position-relative review_box_table_padding"
                        key={index}
                      >
                        <div
                          className="d-flex gap-2"
                          style={{
                            padding: "0.8rem 1rem",
                          }}
                        >
                          <div
                            className="d-flex flex-column w-100"
                            style={{
                              minWidth: 50,
                            }}
                          >
                            <TextStyles.FontSize14px className="three-dots font-weight-500 mb-1">
                              <span className="three-dots">
                                {data.service_name}
                              </span>
                            </TextStyles.FontSize14px>
                            <TextStyles.FontSize14px
                              className="d-none d-md-block"
                              style={{
                                whiteSpace: "break-spaces",
                                maxWidth: "20rem",
                              }}
                            >
                              <span className="truncate-text">{data.desc}</span>
                            </TextStyles.FontSize14px>
                          </div>
                          <TextStyles.FontSize14px
                            className="d-flex justify-content-center w-100"
                            style={{ maxWidth: 60, minWidth: 50 }}
                          >
                            <span className="three-dots">
                              {data.service_rate_count}
                            </span>
                          </TextStyles.FontSize14px>

                          <TextStyles.FontSize14px
                            className="d-flex justify-content-end w-100"
                            style={{ maxWidth: 110, minWidth: 50 }}
                          >
                            <span className="three-dots">
                              {" "}
                              {invoice &&
                                invoice.currency &&
                                invoice.currency.symbol}
                              {data.service_rate
                                ? parseFloat(data.service_rate).toFixed(2)
                                : "00.00"}
                            </span>{" "}
                          </TextStyles.FontSize14px>

                          <TextStyles.FontSize14px
                            className="d-flex justify-content-end w-100"
                            style={{ maxWidth: 120, minWidth: 50 }}
                          >
                            <span className="three-dots">
                              {invoice &&
                                invoice.currency &&
                                invoice.currency.symbol}

                              {data.service_rate && data.service_rate_count
                                ? parseFloat(
                                    data.service_rate * data.service_rate_count
                                  ).toFixed(2)
                                : "00.00"}
                            </span>
                          </TextStyles.FontSize14px>
                        </div>
                        <div
                          className="d-flex"
                          style={{ padding: "0rem 1rem" }}
                        >
                          <div
                            style={{
                              width: "100%",
                              height: "1px",
                              border: "0.5px solid #efefef",
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}

            <div className="review_box_table_padding mt-2">
              <div
                className="d-flex justify-content-end gap-2 fw-semibold"
                style={{
                  padding: "0.5rem 1rem",
                }}
              >
                <TextStyles.FontSize14px>Sub Total</TextStyles.FontSize14px>

                <TextStyles.FontSize14px
                  className="three-dots"
                  style={{ maxWidth: 120, minWidth: 50 }}
                >
                  {invoice && invoice.currency && invoice.currency.symbol}
                  {totalServiceAmount > 0
                    ? parseFloat(totalServiceAmount).toFixed(2)
                    : "00.00"}
                </TextStyles.FontSize14px>
              </div>
            </div>

            <div className="d-flex review_box_table_padding">
              <div
                className="d-flex gap-2 w-100"
                style={{
                  padding: "0.5rem 1rem",
                }}
              >
                <div
                  className="d-flex w-100"
                  style={{
                    minWidth: 50,
                  }}
                />

                <TextStyles.FontSize14px
                  className="d-flex justify-content-end w-100"
                  style={{ maxWidth: 230, minWidth: 50 }}
                >
                  <div
                    style={{
                      width: "100%",
                      height: "1px",
                      border: "0.5px solid #efefef",
                    }}
                  />
                </TextStyles.FontSize14px>
              </div>
            </div>

            {invoice && invoice.taxes.length > 0
              ? invoice.taxes.map((data, index) => {
                  return (
                    <div
                      key={index}
                      className="invoice_taxes review_box_table_padding"
                    >
                      <div
                        className="d-flex gap-2 align-items-center"
                        style={{
                          padding: "0.5rem 1rem",
                        }}
                      >
                        <div
                          className="d-flex w-100"
                          style={{
                            minWidth: 50,
                          }}
                        />

                        <TextStyles.FontSize14px
                          className="d-flex justify-content-end w-100"
                          style={{
                            minWidth: 50,
                            maxWidth: "fit-content",
                          }}
                        >
                          <span className="three-dots">
                            {data.tax_label}
                            {`(${data.percentage}%)`}
                          </span>
                        </TextStyles.FontSize14px>
                        <TextStyles.FontSize14px
                          className="d-flex justify-content-end w-100"
                          style={{ maxWidth: 120, minWidth: 50 }}
                        >
                          <span className="three-dots">
                            {" "}
                            {invoice &&
                              invoice.currency &&
                              invoice.currency.symbol}
                            {totalServiceAmountAfterDiscount > 0
                              ? (
                                  parseFloat(data.percentage) *
                                  parseFloat(totalServiceAmountAfterDiscount) *
                                  0.01
                                ).toFixed(2)
                              : "00.00"}
                          </span>
                        </TextStyles.FontSize14px>
                      </div>
                      <div
                        className="d-flex gap-2 w-100"
                        style={{
                          padding: "0.5rem 1rem",
                        }}
                      >
                        <div
                          className="d-flex w-100"
                          style={{
                            minWidth: 100,
                          }}
                        />

                        <TextStyles.FontSize14px
                          className="d-flex justify-content-end w-100"
                          style={{ maxWidth: 230, minWidth: 50 }}
                        >
                          <div
                            style={{
                              width: "100%",
                              height: "1px",
                              border: "0.5px solid #efefef",
                            }}
                          />
                        </TextStyles.FontSize14px>
                      </div>
                    </div>
                  );
                })
              : null}

            {invoice && invoice.discount ? (
              <div className="invoice_discount review_box_table_padding">
                <div
                  className="d-flex gap-2 align-items-center"
                  style={{
                    padding: "0.5rem 1rem",
                  }}
                >
                  <div
                    className="d-flex w-100"
                    style={{
                      minWidth: 50,
                    }}
                  />
                  <TextStyles.FontSize14px
                    className="d-flex justify-content-end w-100"
                    style={{
                      minWidth: 50,
                      maxWidth: "fit-content",
                    }}
                  >
                    <span className="three-dots">
                      Discount
                      {invoice.discount.percentage
                        ? `(${invoice.discount.percentage}%)`
                        : ""}
                    </span>
                  </TextStyles.FontSize14px>
                  <TextStyles.FontSize14px
                    className="d-flex justify-content-end w-100"
                    style={{
                      minWidth: 50,
                      maxWidth: 120,
                    }}
                  >
                    <span className="three-dots">
                      {invoice && invoice.currency && invoice.currency.symbol}
                      {invoice.discount && invoice.discount.percentage
                        ? parseFloat(
                            totalServiceAmount *
                              parseFloat(invoice.discount.percentage) *
                              0.01
                          ).toFixed(2)
                        : parseFloat(invoice.discount.value).toFixed(2)}
                    </span>
                  </TextStyles.FontSize14px>
                </div>

                <div className="d-flex">
                  <div
                    className="d-flex gap-2 w-100"
                    style={{
                      padding: "0.5rem 1rem",
                    }}
                  >
                    <div
                      className="d-flex w-100"
                      style={{
                        minWidth: 100,
                      }}
                    />

                    <TextStyles.FontSize14px
                      className="d-flex justify-content-end w-100"
                      style={{ maxWidth: 230, minWidth: 50 }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "1px",
                          border: "1px solid #efefef",
                        }}
                      />
                    </TextStyles.FontSize14px>
                  </div>
                </div>
              </div>
            ) : null}

            <div className="review_box_table_padding">
              <div
                className="d-flex gap-2 align-items-center"
                style={{
                  padding: "0.5rem 1rem",
                }}
              >
                <TextStyles.FontSize14px className="d-flex w-100" />

                <TextStyles.FontSize14px
                  className="d-flex justify-content-end w-100"
                  style={{
                    minWidth: 50,
                    fontWeight: 600,
                    maxWidth: "fit-content",
                  }}
                >
                  <span className="three-dots">Amount due</span>
                </TextStyles.FontSize14px>

                <TextStyles.FontSize14px
                  className="d-flex justify-content-end w-100"
                  style={{
                    minWidth: 50,
                    maxWidth: 120,
                    fontWeight: 600,
                  }}
                >
                  <span className="three-dots">
                    {invoice && invoice.currency && invoice.currency.symbol}
                    {totalDueAmount > 0
                      ? parseFloat(totalDueAmount).toFixed(2)
                      : "00.00"}
                  </span>
                </TextStyles.FontSize14px>
              </div>
            </div>

            <div className="review_box_table_padding">
              <div className="d-flex" style={{ padding: "0.5rem 1rem" }}>
                <div
                  style={{
                    width: "100%",
                    height: "1px",
                    border: "0.5px solid #efefef",
                  }}
                />
              </div>
            </div>

            {invoice && invoice.invoice_notes && (
              <div className="invoice_notes review_box_padding">
                <TextStyles.FontSize14px className="fw-semibold mb-1">
                  Notes
                </TextStyles.FontSize14px>

                <TextStyles.FontSize14px style={{ whiteSpace: "break-spaces" }}>
                  {invoice && invoice.invoice_notes}
                </TextStyles.FontSize14px>
              </div>
            )}
          </Styled.SetupBox>
        )}
      </div>
    </>
  );
};

export default InvoicePublic;
